<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref } from 'vue';
import { useThrottle } from '@apparatix/composables';

type Props = {
  minimumHeight?: number;
}

const props = withDefaults(defineProps<Props>(), {
  minimumHeight: 300,
});

const tableContainer = ref<HTMLDivElement | null>(null);
const calculatedHeight = ref<string>();
const isCalculating = ref(true);
const resizeObserver = ref<ResizeObserver>();

const getElementHeight = (e: HTMLElement): number => {
  const style = getComputedStyle(e);
  const computedHeight = parseFloat(style.height);
  if (!isNaN(computedHeight)) {
    return computedHeight;
  }

  const { height } = e.getBoundingClientRect();
  return height;
};

const parseFloatOrDefault = (input: string): number => {
  const result = parseFloat(input);
  return isNaN(result) ? 0 : result;
};

const calculate = () => {
  if (!tableContainer.value) return;
  isCalculating.value = true;

  const parent = tableContainer.value.parentElement;
  const siblings = Array.from(parent.children).filter(child => child !== tableContainer.value) as HTMLElement[];

  const parentStyle = getComputedStyle(parent);
  const ownStyle = getComputedStyle(tableContainer.value);
  const totalHeight = getElementHeight(parent);
  const parentGap = parseFloatOrDefault(parentStyle.gap);
  const parentPaddingTop = parseFloatOrDefault(parentStyle.paddingTop);
  const parentPaddingBottom = parseFloatOrDefault(parentStyle.paddingBottom);
  const ownMarginTop = parseFloatOrDefault(ownStyle.marginTop);
  const ownPaddingTop = parseFloatOrDefault(ownStyle.paddingTop);
  const ownMarginBottom = parseFloatOrDefault(ownStyle.marginBottom);
  const ownPaddingBottom = parseFloatOrDefault(ownStyle.paddingBottom);

  let availableHeight = totalHeight - parentPaddingTop - parentPaddingBottom - ownMarginTop - ownPaddingTop - ownMarginBottom - ownPaddingBottom;

  siblings.forEach(child => {
    const style = getComputedStyle(child);
    const height = getElementHeight(child);
    const marginTop = parseFloatOrDefault(style.marginTop);
    const marginBottom = parseFloatOrDefault(style.marginBottom);

    availableHeight = availableHeight - height - marginTop - marginBottom - parentGap;
  });

  if (availableHeight >= props.minimumHeight) {
    calculatedHeight.value = `${availableHeight}px`;
  } else {
    calculatedHeight.value = `${props.minimumHeight}px`;
  }

  isCalculating.value = false;
};

const onResize = useThrottle(calculate, 250);

onMounted(async () => {
  await nextTick();

  if (!tableContainer.value) {
    calculatedHeight.value = `${props.minimumHeight}px`;
    isCalculating.value = false;

    return;
  }

  calculate();
  resizeObserver.value = new ResizeObserver(onResize);
  resizeObserver.value.observe(tableContainer.value);
});

onUnmounted(() => resizeObserver.value?.disconnect());
</script>
<template>
  <div ref="tableContainer" class="w-full">
    <slot v-if="!isCalculating" :available-height="calculatedHeight" />
  </div>
</template>
